import { EAssemblyOrderStatus } from 'v2/modules/assembly-order/interfaces';
import { EOrderType } from 'v2/modules/shared/interfaces/order.interface';
import { IVerifiableAddressContact } from 'v2/shared/contact/address.interface';

import { IApproval } from '../../approval/interfaces/approval.interface';
import {
  ILocationWarehouseGroup,
  ILocation,
} from '../../location/interfaces/location.interface';
import { ILot } from '../../lot/interfaces/lot.interface';
import { IRfq } from '../../rfq/interfaces/rfq.interface';
import { ICustomFieldValues } from '../../shared/interfaces/custom-field.interface';
import { ITag } from '../../tag/interfaces/tag.interface';
import { IUnitOfMeasure } from '../../unit-of-measure/interfaces/unit-of-measure.interface';

import { ICompany } from './../../client/interfaces/client-company.interface';

export interface ISalesOrder {
  id :number;
  createdAt :string;
  updatedAt :string;
  status :string;
  orderId :number;
  orderNumber :string;
  orderKey :string;
  orderDate :string;
  createDate :string;
  modifyDate :string;
  paymentDate ?:any;
  shipByDate ?:any;
  doNotShipBefore ?:any;
  orderStatus :string;
  customerId ?:any;
  customerUsername ?:any;
  customerEmail ?:any;
  billTo :IVerifiableAddressContact;
  shipTo :IVerifiableAddressContact;
  items ?:any;
  orderTotal :number;
  amountPaid :number;
  taxAmount :number;
  shippingAmount :number;
  customerNotes ?:any;
  internalNotes ?:any;
  gift :boolean;
  giftMessage ?:any;
  paymentMethod ?:any;
  requestedShippingService ?:any;
  carrierCode ?:any;
  serviceCode ?:any;
  packageCode ?:any;
  confirmation ?:any;
  shipDate ?:any;
  holdUntilDate ?:any;
  weight ?:any;
  dimensions ?:any;
  insuranceOptions ?:any;
  internationalOptions ?:any;
  advancedOptions ?:any;
  tagIds ?:any;
  tags :ITag[];
  userId ?:any;
  externallyFulfilled :boolean;
  externallyFulfilledBy ?:any;
  labelMessages ?:any;
  isPosted :boolean;
  postedBy :number;
  notSync :boolean;
  salesPosted :boolean;
  splitOrder :boolean;
  skipInventory :boolean;
  skipSalesReport :boolean;
  rowType :number;
  channel :any;
  channelId :number;
  totalOrderCost :number;
  totalOrderQuantity :number;
  orderCostDetails ?:any;
  source :string;
  shipstationOrderId ?:any;
  type :string;
  parentId ?:any;
  shippingRevenue ?:any;
  productTotal ?:any;
  totalDiscount ?:any;
  picklist :any;
  pickingStatus :string;
  customFieldValues :ICustomFieldValues[];
  orderItems ?:ISalesOrderItem[];
  warehouseGroup ?:ILocationWarehouseGroup[];
  trackingNumbers :IOrderTrackingNumber[];
  isRowMaster ?:boolean;
  approval :IApproval;
  isExpanded ?:boolean;
  isIncluded ?:boolean;
  salesOrderShipments ?:ISalesOrderShipment[];
  customerPoNumber ?:number;
  businessAccount ?:ICompany;
  sales_order_shipments :IOldSalesOrderShipments[];
  warehouse_group ?:any;
  channelRelation ?:any;
  is_posted ?:number;
  destination_reference_identifier ?:string;
  destinationReferenceIdentifier ?:string;
  hasPushOrderIntegrationMapping ?:boolean;
  hasNoRemainingFulfillmentQuantity ?:boolean;
  warehouseGroupId ?:number;
  warehouse_group_id ?:number;
  isShippingAddressOverridden ?:boolean;
}

export interface ISalesOrderItem {
  id :number;
  createdAt :string;
  updatedAt :string;
  status :string;
  mtShipstationOrderId :number;
  orderItemId :number;
  lineItemKey :string;
  sku :string;
  name :string;
  imageUrl ?:any;
  weight ?:any;
  quantity :number;
  unitPrice :number;
  taxAmount ?:any;
  shippingAmount ?:any;
  warehouseLocation ?:any;
  options ?:any;
  productId :number;
  fulfillmentSku ?:any;
  adjustment ?:any;
  upc ?:any;
  createDate :string;
  modifyDate :string;
  orderDate :string;
  paymentDate ?:any;
  orderStatus :string;
  stockId :number;
  kitId :number;
  nonPhysicalItem :boolean;
  ignoreItem :boolean;
  quantityInvoiced :number;
  quantityShipped :number;
  quantityCancelled :number;
  orderedQuantity :number;
  baseUomId :number;
  baseUom :IUnitOfMeasure | null;
  orderedUomId :number;
  orderedUom :IUnitOfMeasure | null;
  rfqId :number;
  rfq :IRfq;
  totalReturnedBaseQuantity :number;
  parent_id ?:any;
  isIncluded ?:boolean;
  estimatedShipDate ?:Date | string;
  ignore_item ?:boolean;
  errorMessage ?:string;
  discount ?:number;
  customFieldValues :ICustomFieldValues[];
  quantityOverridden ?:number;
  quantityOriginalOverriddenValue ?:number;
  quantityFulfilled ?:number;
  quantity_fulfilled ?:number;
  quantityRemaining ?:number;
  lot ?:any;
  lotId :number;
}

export interface IOrderTrackingNumber {
  id :number;
  createdAt :string;
  updatedAt :string;
  salesOrderId :number;
  trackingNumber :string;
}

export interface IOrderPickingPreviewDialogInterface {
  fulfillmentStatus :boolean;
  orderItems :IOrderItemPickingInterface[];
  salesOrder :ISalesOrder;
  pickingItems :IPickingItemPreviewDialogInterface[];
}

export interface IPickingItemPreviewDialogInterface {
  picklistId :number;
  availableQuantity :number;
  pickedQuantity :number;
  orderId :number;
  orderItemId :number;
  locationId :number;
  warehouseId :number;
  rfqId :number;
  lotId :number;
  uomId :number;
  parentId :number;
  location :ILocation;
  warehouse :ILocation;
  lot :ILot;
  uom :IUnitOfMeasure;
  rfq :IRfq;
  orderedQuantity :number;
}

export interface IOrderItemPickingInterface extends ISalesOrderItem {
  isFulfilled :boolean;
}

export enum EOrderTypeId {
  DEFAULT = 1,
}

export enum EOrderFrequency {
  RECURRING = 'recurring',
  ONETIME = 'one_time',
}

export enum EOrderDeliveryType {
  STANDARD = 'standard',
  WAREHOUSE = 'warehouse',
  LOCAL_DELIVERY = 'local_delivery',
}

export enum EOrderStatus {
  ALL = '',
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

export enum EItemType {
  PRODUCT = 'PRODUCT',
  KIT = 'KIT',
}

export enum ESalesOrderStatus {
  SHIPPED = 'shipped',
  AWAITING_SHIPMENT = 'awaiting_shipment',
  PENDING = 'pending',
  CANCELLED = 'cancelled',
  PARTIALLY_SHIPPED = 'partially_shipped',
  ON_HOLD = 'on_hold',
}

export enum EOrderPullUpdatesErrors {
  NOT_POSTED = 'Order is not allocated.',
  IS_SHIPSTATION = 'Not a Shipstation order or lacks Shipstation ID.',
  IS_SHIPKEY_OMS_ENABLED = 'Not from Shipkey or Shipkey OMS is disabled.',
}

export enum EPullUpdateStatus {
  GETTING_UPDATES = 'Getting updates',
  UPDATED = 'UPDATED',
  FAILED = 'FAILED',
}
export enum ESalesOrderSource {
  TRANSFER_ORDER = 'transfer_order',
  LUMINOUS = 'luminous',
  SHIPSTATION = 'shipstation',
  EDI = 'edi',
  SALES_ORDER = 'sales_order',
  NUORDER = 'nuorder',
  SHIPKEY = 'shipkey',
  SHOPIFY = 'shopify',
  WOOCOMMERCE = 'woocommerce',
  AMAZON = 'amazon',
  API = 'api',
  QUICKBOOKS = 'quickbooks',
  API2CART = 'api2cart',
  ECARTAPI = 'ecartapi',
  TRACKSTAR = 'trackstar',
}

export interface IOrderShippabilityErrors {
  missingBillingDetails :boolean;
  missingShippingDetails :boolean;
  isCancelledOrder :boolean;
  hasShipstationOrderId :boolean;
  isShipstation :boolean;
  isNotPosted :boolean;
  isNotShippable :boolean;
}

export interface ISalesOrderShipment {
  id :number;
  orderNumber :string;
  orderKey :string;
  sourceReferenceIdentifier :string;
  orderStatus :string;
}

export enum EExportType {
  PDF = 'pdf',
  CSV = 'csv',
  XLSX = 'xlsx',
}

interface IOldSalesOrderShipments {
  id :number;
  created_at :string;
  updated_at :string;
  salesOrderId :number;
  orderId :number;
  source_reference_identifier :string;
  orderNumber :string;
  orderKey :string;
  orderDate :string;
  createDate :string;
  modifyDate :string;
  paymentDate :null;
  shipByDate :null;
  orderStatus :string;
  customerId :null;
  customerUsername :null;
  customerEmail :string;
  billTo :string;
  shipTo :string;
  items :string;
  orderTotal :number;
  amountPaid :number;
  taxAmount :number;
  shippingAmount :number;
  customerNotes :null;
  internalNotes :null;
  gift :number;
  giftMessage :null;
  paymentMethod :null;
  requestedShippingService :null;
  carrierCode :null;
  serviceCode :null;
  packageCode :null;
  confirmation :string;
  shipDate :null;
  holdUntilDate :null;
  weight :string;
  dimensions :null;
  insuranceOptions :string;
  internationalOptions :string;
  advancedOptions :string;
  tagIds :null;
  userId :null;
  externallyFulfilled :null;
  externallyFulfilledBy :null;
  labelMessages :null;
  deleted_at :null;
  warehouse_group_id :number;
  destination_oms_id :number;
  destination_oms_store_id :number;
  warehouse_group :IWarehousegroup;
  destination_oms :IDestinationoms;
}

interface IDestinationoms {
  id :number;
  label :string;
  app :string;
  auth_type :string;
  account_type :string;
  credentials :ICredentials;
  created_by :number;
  updated_by :number;
  created_at :string;
  updated_at :string;
  deleted_at :null;
  meta :null;
}

interface ICredentials {
  api_key :string;
  api_secret :string;
}

interface IWarehousegroup {
  id :number;
  uuid :string;
  label :string;
  description :string;
  created_at :string;
  updated_at :string;
  deleted_at :null;
  created_by :number;
  updated_by :number;
  deleted_by :null;
  scan_to_verify :boolean;
  pick_and_pack_enabled :boolean;
  push_order :boolean;
  b2b_inventory_numbers_enabled :boolean;
  externally_managed :boolean;
  externally_managed_app_integration_account_id :number;
  externally_managed_warehouse :null;
  pull_stocks :boolean;
  pull_shipments :boolean;
  app_integration_account_inventory_location_id :null;
  combine_kit_inventory :boolean;
}

export interface IOrderCreateFromItems {
  sourceOrderType :EOrderType;
  destinationOrderType :string;
  orderItems :IOrderCreateItem[];
  sourceWarehouseId ?:number;
  orderStatus ?:EAssemblyOrderStatus;
  combineIdenticalSkus ?:boolean;
  alternateIds ?:string[];
}

export interface IOrderCreateItem {
  orderId :number;
  orderItemId :number;
  itemId :number;
  itemType :string;
}

export enum EOrderTaggableType {
  SALES_ORDER_LINE_ITEM = 'sales_order_line_item',
  ASSEMBLY_ORDER_LINE_ITEM = 'assembly_order_line_item',
}
